import { supabase } from '../lib/supabase';

const EDGE_FUNCTION_URL = `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-email`;

const APP_BASE_URL = 'https://mibuy2878.builtwithrocket.new';

/**
 * Build a pre-populated one-click unsubscribe URL for the given recipient email.
 */
const buildUnsubscribeUrl = (email) => {
  if (!email) return `${APP_BASE_URL}/unsubscribe`;
  return `${APP_BASE_URL}/unsubscribe?email=${encodeURIComponent(email?.trim()?.toLowerCase())}`;
};

const callEmailFunction = async (type, to, data = {}) => {
  try {
    const { data: sessionData } = await supabase?.auth?.getSession();
    const token = sessionData?.session?.access_token;

    const response = await fetch(EDGE_FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ type, to, data, resendApiKey: import.meta.env?.VITE_RESEND_API_KEY }),
    });

    const result = await response?.json();
    if (!response?.ok) {
      throw new Error(result?.error || 'Email send failed');
    }
    return { success: true, id: result?.id };
  } catch (err) {
    console.error(`[emailService] Failed to send ${type} email:`, err);
    return { success: false, error: err?.message };
  }
};

/**
 * Fetch email/notification preference flags for a user by email address.
 * Returns null if the user is not found or the query fails.
 */
const getUserPreferences = async (email) => {
  if (!email) return null;
  try {
    const { data, error } = await supabase
      ?.from('user_profiles')
      ?.select('email_marketing, email_product_updates, email_quote_alerts, email_weekly_digest, notif_new_quotes, notif_milestone_updates, notif_messages, notif_vendor_activity, notif_system_alerts, data_opt_out')
      ?.eq('email', email)
      ?.maybeSingle();
    if (error) return null;
    return data;
  } catch {
    return null;
  }
};

/**
 * Send a welcome email after signup.
 * Always sent — transactional, no opt-out.
 */
export const sendWelcomeEmail = (to, data = {}) =>
  callEmailFunction('welcome', to, { ...data, unsubscribeUrl: buildUnsubscribeUrl(to) });

/**
 * Send a stack completion notification.
 * Gated by: notif_milestone_updates
 */
export const sendStackCompleteEmail = async (to, data = {}) => {
  const prefs = await getUserPreferences(to);
  if (prefs && prefs?.notif_milestone_updates === false) {
    console.info(`[emailService] stack-complete suppressed for ${to} (notif_milestone_updates=false)`);
    return { success: false, suppressed: true };
  }
  return callEmailFunction('stack-complete', to, { ...data, unsubscribeUrl: buildUnsubscribeUrl(to) });
};

/**
 * Send a stack share email.
 * Gated by: email_product_updates
 */
export const sendStackShareEmail = async (to, data = {}) => {
  const prefs = await getUserPreferences(to);
  if (prefs && prefs?.email_product_updates === false) {
    console.info(`[emailService] stack-share suppressed for ${to} (email_product_updates=false)`);
    return { success: false, suppressed: true };
  }
  return callEmailFunction('stack-share', to, { ...data, unsubscribeUrl: buildUnsubscribeUrl(to) });
};

/**
 * Send a quote submitted notification email to the project owner.
 * Gated by: notif_new_quotes
 */
export const sendQuoteSubmittedEmail = async (to, data = {}) => {
  const prefs = await getUserPreferences(to);
  if (prefs && prefs?.notif_new_quotes === false) {
    console.info(`[emailService] quote-submitted suppressed for ${to} (notif_new_quotes=false)`);
    return { success: false, suppressed: true };
  }
  return callEmailFunction('quote-submitted', to, { ...data, unsubscribeUrl: buildUnsubscribeUrl(to) });
};

/**
 * Send a vendor quote request email to a vendor.
 * Gated by: notif_vendor_activity (only if the vendor has a registered profile)
 */
export const sendVendorQuoteRequestEmail = async (to, data = {}) => {
  const prefs = await getUserPreferences(to);
  if (prefs && prefs?.notif_vendor_activity === false) {
    console.info(`[emailService] vendor-quote-request suppressed for ${to} (notif_vendor_activity=false)`);
    return { success: false, suppressed: true };
  }
  return callEmailFunction('vendor-quote-request', to, { ...data, unsubscribeUrl: buildUnsubscribeUrl(to) });
};

/**
 * Send a reminder email to a vendor who hasn't submitted a quote yet.
 * Gated by: notif_vendor_activity (only if the vendor has a registered profile)
 */
export const sendVendorQuoteReminderEmail = async (to, data = {}) => {
  const prefs = await getUserPreferences(to);
  if (prefs && prefs?.notif_vendor_activity === false) {
    console.info(`[emailService] vendor-quote-reminder suppressed for ${to} (notif_vendor_activity=false)`);
    return { success: false, suppressed: true };
  }
  return callEmailFunction('vendor-quote-reminder', to, { ...data, unsubscribeUrl: buildUnsubscribeUrl(to) });
};

/**
 * Send a vendor quote request email to a vendor with full stack details.
 * Gated by: notif_vendor_activity (only if the vendor has a registered profile)
 */
export const sendVendorQuoteRequestEmailDetailed = async (to, data = {}) => {
  const prefs = await getUserPreferences(to);
  if (prefs && prefs?.notif_vendor_activity === false) {
    console.info(`[emailService] vendor-quote-request-detailed suppressed for ${to} (notif_vendor_activity=false)`);
    return { success: false, suppressed: true };
  }
  return callEmailFunction('vendor-quote-request-detailed', to, { ...data, unsubscribeUrl: buildUnsubscribeUrl(to) });
};

/**
 * Send a deposit confirmation email.
 * Transactional — always sent (no opt-out).
 * Pass isVendor=true in data to send the vendor variant.
 */
export const sendDepositConfirmationEmail = (to, data = {}) =>
  callEmailFunction('deposit-confirmation', to, { ...data, unsubscribeUrl: buildUnsubscribeUrl(to) });

/**
 * Send a completion payment reminder email when all milestones are complete.
 * Transactional — always sent (no opt-out).
 * Pass isVendor=true in data to send the vendor variant.
 */
export const sendCompletionPaymentReminderEmail = (to, data = {}) =>
  callEmailFunction('completion-payment-reminder', to, { ...data, unsubscribeUrl: buildUnsubscribeUrl(to) });

/**
 * Send a payment success notification.
 * Transactional — always sent (no opt-out).
 * Pass isVendor=true in data to send the vendor variant.
 */
export const sendPaymentSuccessEmail = (to, data = {}) =>
  callEmailFunction('payment-success', to, { ...data, unsubscribeUrl: buildUnsubscribeUrl(to) });

/**
 * Send a payment failure notification.
 * Transactional — always sent (no opt-out).
 * Pass isVendor=true in data to send the vendor variant.
 */
export const sendPaymentFailureEmail = (to, data = {}) =>
  callEmailFunction('payment-failure', to, { ...data, unsubscribeUrl: buildUnsubscribeUrl(to) });
